import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, User as ApiUser, FileCategory, FileDocument, CategoryWithDocuments } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import CategoryManagement from './CategoryManagement';
import DocumentUpload from './DocumentUpload';
import CircularManagement from './CircularManagement';
import ContactManagement from './ContactManagement';
import OtherFileManagement from './OtherFileManagement';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  LogOut, 
  User,
  BarChart3,
  CreditCard,
  PiggyBank,
  Upload,
  FileText,
  FolderOpen,
  Home,
  Download,
  Trash2,
  Eye,
  ExternalLink,
  Menu,
  X,
  Clock,
  Sun,
  Moon,
  Cloud,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  Plus,
  Search,
  Filter,
  MoreVertical,
  FileDown,
  AlertCircle,
  CheckCircle,
  Info,
  Users,
  Shield,
  BookOpen,
  FileCheck,
  ClipboardList,
  Mail,
  Phone,
  MapPin,
  Globe,
  Lock,
  Unlock,
  EyeOff,
  Save,
  RotateCcw,
  Zap,
  Activity,
  Folder
} from 'lucide-react';

interface FileItem {
  id: string;
  name: string;
  size: string;
  type: string;
  uploadDate: Date;
  url?: string;
  category: string;
  subcategory: string;
}



const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('home');
  const [downloadsSubTab, setDownloadsSubTab] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());


  // Contact notification system
  const [lastContactCheck, setLastContactCheck] = useState<Date>(new Date());
  const [contactPollingInterval, setContactPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [newContactNotifications, setNewContactNotifications] = useState<Array<{id: number, contactId: number, name: string, subject: string, timestamp: Date}>>([]);
  const [showContactBanner, setShowContactBanner] = useState(false);
  const [latestContact, setLatestContact] = useState<{name: string, subject: string} | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  // User profile data from backend
  const [userProfile, setUserProfile] = useState<ApiUser | null>(null);
  const [editingData, setEditingData] = useState<{
    strName: string;
    bolsActive: boolean;
  }>({
    strName: '',
    bolsActive: true
  });

  // File management state
  const [categories, setCategories] = useState<FileCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<CategoryWithDocuments | null>(null);
  const [documents, setDocuments] = useState<FileDocument[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileStatistics, setFileStatistics] = useState({
    totalDownloads: 0,
    totalCategories: 0,
    totalCirculars: 0,
    totalContacts: 0,
    unreadContacts: 0,
    documentsThisMonth: 0,
    categoriesThisWeek: 0,
    circularsToday: 0,
    downloadsToday: 0,
    downloadsThisWeek: 0,
    recentDownloads: [],
    fileTypeDistribution: []
  });

  // Dashboard statistics state
  const [loadingStats, setLoadingStats] = useState(false);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);



  // Load categories with file counts
  const loadCategories = async () => {
    try {
      const data = await apiService.getAllCategories();
      
      // Load file counts for each category
      const categoriesWithCounts = await Promise.all(
        data.map(async (category) => {
          try {
            const documents = await apiService.getDownloadsByCategory(category.strGUID);
            return {
              ...category,
              downloadCount: documents.length
            };
          } catch (error) {
            // If there's an error getting documents for a category, set count to 0
            return {
              ...category,
              downloadCount: 0
            };
          }
        })
      );
      
      setCategories(categoriesWithCounts);
    } catch (error) {
      // Handle error silently or show user-friendly message
    }
  };

  // Load categories on component mount
  useEffect(() => {
    if (user && localStorage.getItem('token')) {
      loadCategories();
    }
  }, [user]);

  // Reload statistics when documents or categories change
  useEffect(() => {
    if (user) {
      loadDashboardStatistics();
    }
  }, [user, documents, categories]);

  // Start contact polling on component mount
  useEffect(() => {
    startContactPolling();
    
    // Cleanup on unmount
    return () => {
      stopContactPolling();
    };
  }, []);

  // Update lastContactCheck when new contacts are found
  useEffect(() => {
    if (newContactNotifications.length > 0) {
      const latestContact = newContactNotifications[newContactNotifications.length - 1];
      setLastContactCheck(latestContact.timestamp);
    }
  }, [newContactNotifications]);

  const loadDashboardStatistics = async () => {
    try {
      setLoadingStats(true);
      setError(null);

      // Get file management statistics
      const fileStats = await apiService.getStatistics();
      setFileStatistics(fileStats);

      // Get circulars count
      let circularsCount = 0;
      try {
        const circularsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://localhost:7222/api'}/circular`);
        if (circularsResponse.ok) {
          const circulars = await circularsResponse.json();
          circularsCount = circulars.length;
        }
      } catch (error) {
        // console.error('Error loading circulars:', error);
      }

      // Get contacts count
      let contactsCount = 0;
      let unreadContactsCount = 0;
      try {
        const token = localStorage.getItem('token');
        const contactsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://localhost:7222/api'}/contact`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (contactsResponse.ok) {
          const contactsData = await contactsResponse.json();
          contactsCount = contactsData.totalCount || 0;
          unreadContactsCount = contactsData.unreadCount || 0;
        }
      } catch (error) {
        // console.error('Error loading contacts:', error);
      }

      // Calculate trends
      const now = new Date();
      const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const documentsThisMonth = documents.filter(doc => 
        new Date(doc.dtUploadDate) >= oneMonthAgo
      ).length;

      const categoriesThisWeek = categories.filter(cat => 
        new Date(cat.dtCreateDate) >= oneWeekAgo
      ).length;

      const circularsToday = 0; // This would need to be calculated from circular upload dates

      setFileStatistics({
        totalDownloads: fileStats.TotalDownloads || 0,
        totalCategories: fileStats.TotalCategories || categories.length,
        totalCirculars: circularsCount,
        totalContacts: contactsCount,
        unreadContacts: unreadContactsCount,
        documentsThisMonth: fileStats.ThisMonthDownloads || documentsThisMonth,
        categoriesThisWeek,
        circularsToday,
        downloadsToday: fileStats.TodayDownloads || 0,
        downloadsThisWeek: fileStats.ThisWeekDownloads || 0,
        recentDownloads: fileStats.TopDownloadedItems || [],
        fileTypeDistribution: fileStats.DownloadsByType || []
      });

      setSuccess('Dashboard statistics updated successfully');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);

    } catch (error) {
      // console.error('Error loading dashboard statistics:', error);
      setError(`Failed to load dashboard statistics: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
      
      // Set fallback values
      setFileStatistics({
        totalDownloads: 0,
        totalCategories: categories.length,
        totalCirculars: 0,
        totalContacts: 0,
        unreadContacts: 0,
        documentsThisMonth: 0,
        categoriesThisWeek: 0,
        circularsToday: 0,
        downloadsToday: 0,
        downloadsThisWeek: 0,
        recentDownloads: [],
        fileTypeDistribution: []
      });
    } finally {
      setLoadingStats(false);
    }
  };

  // Contact notification polling function
  const startContactPolling = () => {
    // Poll every 30 seconds instead of every 1 second
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://localhost:7222/api'}/contact`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          const contacts = data.contacts || [];
          
          // Only check for new contacts since last check
          const newContacts = contacts.filter((contact: any) => 
            new Date(contact.dtCreatedDate) > lastContactCheck
          );
          
          if (newContacts.length > 0) {
            setNewContactNotifications(prev => [
              ...prev,
              ...newContacts.map((contact: any) => ({
                id: Date.now() + Math.random(),
                contactId: contact.strGUID,
                name: contact.strName,
                subject: contact.strSubject,
                timestamp: new Date(contact.dtCreatedDate)
              }))
            ]);
            
            setLatestContact({
              name: newContacts[0].strName,
              subject: newContacts[0].strSubject
            });
            
            setShowContactBanner(true);
            
            // Show success message
            setSuccess(`📬 New message received from ${newContacts[0].strName}`);
            setShowToast(true);
            setTimeout(() => setShowToast(false), 5000);
          }
        }
      } catch (error) {
        // Silently handle errors to avoid console spam
      }
    }, 30000); // Changed from 1000 to 30000 (30 seconds)
    
    setContactPollingInterval(interval);
  };

  // Stop contact polling
  const stopContactPolling = () => {
    if (contactPollingInterval) {
      clearInterval(contactPollingInterval);
      setContactPollingInterval(null);
    }
  };

  const loadCategoryDocuments = async (categoryId: string) => {
    try {
      setLoadingFiles(true);
      setFileError(null);
      
      const categoryData = await apiService.getCategoryWithDocuments(categoryId);
      
      if (categoryData.documents.length === 0) {
        // No documents found for this category
      } else {
        // Log each document for debugging
        categoryData.documents.forEach((doc, index) => {
          // Document loaded
        });
      }
      
      setSelectedCategory(categoryData);
      setDocuments(categoryData.documents);
    } catch (error) {
      // console.error('Error loading documents:', error);
      setFileError(`Failed to load documents: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Fallback to mock data for testing
      const mockDocuments: FileDocument[] = [
        {
          strGUID: '1',
          strCategoryGUID: categoryId,
          strCategoryName: 'Sample Category',
          strFileName: 'sample-document.pdf',
          strDisplayName: 'Sample Document',
          strFilePath: '/uploads/downloads/sample-document.pdf',
          strFileType: '.pdf',
          fileSize: 1024000,
          bolisActive: true,
          dtUploadDate: new Date().toISOString(),
          dtModifyDate: new Date().toISOString(),
          strUploadBy: 'mock-user',
          strModifiedBy: 'mock-user',
          downloadCount: 5
        },
        {
          strGUID: '2',
          strCategoryGUID: categoryId,
          strCategoryName: 'Sample Category',
          strFileName: 'another-document.pdf',
          strDisplayName: 'Another Document',
          strFilePath: '/uploads/downloads/another-document.pdf',
          strFileType: '.pdf',
          fileSize: 2048000,
          bolisActive: true,
          dtUploadDate: new Date().toISOString(),
          dtModifyDate: new Date().toISOString(),
          strUploadBy: 'mock-user',
          strModifiedBy: 'mock-user',
          downloadCount: 3
        }
      ];
      
      const mockCategoryData: CategoryWithDocuments = {
        strGUID: categoryId,
        strName: 'Sample Category',
        bolIsActive: true,
        intSortOrder: 1,
        documents: mockDocuments
      };
      
      setSelectedCategory(mockCategoryData);
      setDocuments(mockDocuments);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDocumentDownload = async (documentId: string) => {
    try {
      await apiService.downloadDocument(documentId);
    } catch (error) {
      // console.error('Error downloading document:', error);
      window.alert('Failed to download document');
      
      // Mock download for testing
      // console.log('Mock download for document:', documentId);
      window.alert('Mock download completed for document: ' + documentId);
    }
  };

  const handleDocumentDelete = async (documentId: string) => {
    try {
      // Use window.confirm instead of confirm to avoid ESLint error
      if (window.confirm('Are you sure you want to delete this document?')) {
        await apiService.deleteDownload(documentId);
        
        // Refresh the documents list
        if (selectedCategory) {
          await loadCategoryDocuments(selectedCategory.strGUID);
        }
        
        // Refresh categories to update file counts
        await loadCategories();
        
        window.alert('Document deleted successfully');
      }
    } catch (error) {
      // console.error('Error deleting document:', error);
      window.alert('Failed to delete document');
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  // Load user profile when modal opens
  const loadUserProfile = async () => {
    if (!user?.strGUID) return;
    
    setLoading(true);
    setError(null);
    try {
      const profile = await apiService.getUserById(user.strGUID);
      setUserProfile(profile);
      setEditingData({
        strName: profile.strName || '',
        bolsActive: profile.bolsActive
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle profile modal open
  const handleProfileModalOpen = () => {
    setShowProfileModal(true);
    loadUserProfile();
  };

  // Handle profile update
  const handleProfileUpdate = async () => {
    if (!user?.strGUID || !userProfile) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const updatedProfile = await apiService.updateUserProfile(user.strGUID, {
        strName: editingData.strName,
        bolsActive: editingData.bolsActive
      });
      
      setUserProfile(updatedProfile);
      setEditingProfile(false);
      setSuccess('Profile updated successfully! Database has been updated.');
      
      // Update the auth context user data if needed
      // You might need to add a method to update user in AuthContext
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle profile delete
  const handleProfileDelete = async () => {
    if (!user?.strGUID) return;
    
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await apiService.deleteUser(user.strGUID);
      setShowProfileModal(false);
      handleLogout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };



  const handleFileAction = (action: string, document: FileDocument) => {
    switch (action) {
      case 'view':
        // For now, just download the file
        handleDocumentDownload(document.strGUID);
        break;
      case 'download':
        handleDocumentDownload(document.strGUID);
        break;
      case 'delete':
        // This would require admin privileges
        // console.log('Delete action not implemented for users');
        break;
    }
  };

  // Handle contact notification click
  const handleContactNotificationClick = (contactId: number) => {
    // Navigate to contact management tab
    setActiveTab('contacts');
    
    // You could also scroll to the specific contact if needed
    // console.log('Navigating to contact:', contactId);
  };

  const getFirstName = (fullName: string) => {
    return fullName.split(' ')[0] || fullName;
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getWeatherIcon = () => {
    const hour = currentTime.getHours();
    if (hour >= 6 && hour < 18) return <Sun className="h-6 w-6 text-yellow-500" />;
    return <Moon className="h-6 w-6 text-blue-500" />;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatNotificationTime = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getNotificationIcon = (type: 'info' | 'warning' | 'success') => {
    switch (type) {
      case 'info': return Info;
      case 'warning': return AlertCircle;
      case 'success': return CheckCircle;
      default: return Info;
    }
  };

  const getNotificationColor = (type: 'info' | 'warning' | 'success') => {
    switch (type) {
      case 'info': return 'text-blue-600 bg-blue-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'success': return 'text-green-600 bg-green-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const filteredDocuments = documents.filter(document => {
    const matchesSearch = document.strDisplayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (document.strCategoryName && document.strCategoryName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategoryFilter === 'all' || document.strCategoryGUID === selectedCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  const sidebarItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'downloads', label: 'Downloads', icon: Download },
    { id: 'circulars', label: 'Circulars', icon: FileText },
    { id: 'other-files', label: 'Other Files', icon: FolderOpen },
    { 
      id: 'contacts', 
      label: 'Contacts', 
      icon: Mail,
      badge: newContactNotifications.length > 0 ? newContactNotifications.length : undefined
    },
  ];

  const downloadsSubItems = [
    { id: 'browse', label: 'Browse Files', icon: FileText },
    { id: 'categories', label: 'Create Categories', icon: Plus },
    { id: 'upload', label: 'Upload Documents', icon: Upload },
  ];

  const renderHome = () => (
    <div className="space-y-6">
      {/* Simple Hero Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="flex flex-col lg:flex-row items-center justify-between">
          <div className="text-center lg:text-left mb-6 lg:mb-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {getGreeting()}, {user?.strName ? getFirstName(user.strName) : 'User'}! 👋
            </h1>
            <p className="text-gray-600 dark:text-slate-400">
              Welcome to your MoneyCare Financial Dashboard
            </p>
            <div className="flex items-center justify-center lg:justify-start mt-4 space-x-4 text-sm text-gray-500 dark:text-slate-400">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>{formatTime(currentTime)}</span>
              </div>
              <div className="flex items-center space-x-2">
                {getWeatherIcon()}
                <span>{formatDate(currentTime)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <Button 
              onClick={() => setShowProfileModal(true)}
              variant="outline"
              className="border-gray-300 dark:border-slate-600"
            >
              <User className="h-4 w-4 mr-2" />
              Profile
            </Button>
            <Button 
              onClick={handleLogout}
              variant="outline" 
              className="border-gray-300 dark:border-slate-600"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg">
              <Folder className="h-7 w-7" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Categories</p>
              <p className="text-2xl font-bold text-gray-900">{fileStatistics.totalCategories || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Active file categories</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg">
              <FileText className="h-7 w-7" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Circulars</p>
              <p className="text-2xl font-bold text-gray-900">{fileStatistics.totalCirculars || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Published circulars</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg">
              <Mail className="h-7 w-7" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Contact Messages</p>
              <p className="text-2xl font-bold text-gray-900">{fileStatistics.totalContacts || 0}</p>
              <p className="text-xs text-blue-600 font-medium mt-1">{fileStatistics.unreadContacts || 0} unread</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <Zap className="h-5 w-5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Button 
              variant="outline" 
              onClick={() => setActiveTab('downloads')}
              className="h-24 flex flex-col items-center justify-center space-y-2 p-2 border-2 hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
            >
              <Download className="h-6 w-6 text-blue-600" />
              <span className="text-sm text-center">Manage Downloads</span>
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => setActiveTab('circulars')}
              className="h-24 flex flex-col items-center justify-center space-y-2 p-2 border-2 hover:border-orange-400 hover:bg-orange-50 transition-all duration-200"
            >
              <FileText className="h-6 w-6 text-orange-600" />
              <span className="text-sm text-center">Manage Circulars</span>
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => setActiveTab('other-files')}
              className="h-24 flex flex-col items-center justify-center space-y-2 p-2 border-2 hover:border-green-400 hover:bg-green-50 transition-all duration-200"
            >
              <FolderOpen className="h-6 w-6 text-green-600" />
              <span className="text-sm text-center">Other Files</span>
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => setActiveTab('contacts')}
              className="h-24 flex flex-col items-center justify-center space-y-2 p-2 border-2 hover:border-purple-400 hover:bg-purple-50 transition-all duration-200"
            >
              <Mail className="h-6 w-6 text-purple-600" />
              <span className="text-sm text-center">View Messages</span>
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <div className="p-2 rounded-lg bg-green-50 text-green-600">
              <Activity className="h-5 w-5" />
            </div>
          </div>
          <div className="space-y-4">
            {fileStatistics.recentDownloads?.slice(0, 4).map((download: any, index: number) => (
              <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                  <Download className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{download.Name}</p>
                  <p className="text-sm text-gray-600">{download.Category}</p>
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">{download.UploadDate}</span>
              </div>
            ))}
            {(!fileStatistics.recentDownloads || fileStatistics.recentDownloads.length === 0) && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Activity className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Simple Quick Actions */}
      <Card className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 shadow-sm">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-white">Quick Actions</CardTitle>
          <CardDescription className="text-gray-600 dark:text-slate-400">
            Access your most used features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-16 flex flex-col space-y-2 p-2 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700"
              onClick={() => setActiveTab('downloads')}
            >
              <Download className="h-5 w-5" />
              <span className="text-sm">Downloads</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-16 flex flex-col space-y-2 p-2 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700"
              onClick={() => setActiveTab('circulars')}
            >
              <FileText className="h-5 w-5" />
              <span className="text-sm">Circulars</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-16 flex flex-col space-y-2 p-2 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700"
              onClick={() => setActiveTab('other-files')}
            >
              <FolderOpen className="h-5 w-5" />
              <span className="text-sm">Other Files</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-16 flex flex-col space-y-2 p-2 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700"
              onClick={handleProfileModalOpen}
            >
              <User className="h-5 w-5" />
              <span className="text-sm">Profile</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Simple Recent Activity */}
      <Card className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 shadow-sm">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-white">Recent Activity</CardTitle>
          <CardDescription className="text-gray-600 dark:text-slate-400">
            Latest updates and notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Activity className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-slate-400">No recent activity</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Simple Contact Messages */}
      <Card className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-gray-900 dark:text-white">
            <Mail className="h-4 w-4" />
            <span>Contact Messages</span>

          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-slate-400">
            Manage customer inquiries
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-l-4 border-green-500 pl-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-r-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">All caught up! 🎉</p>
                  <p className="text-xs text-gray-600 dark:text-slate-400">No unread contact messages at the moment</p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="mt-2 w-full sm:w-auto"
                  onClick={() => setActiveTab('contacts')}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  View All Messages
                </Button>
              </div>
            </div>

            <div className="text-center pt-2">
              <Button 
                variant="outline" 
                onClick={() => setActiveTab('contacts')}
                className="w-full"
              >
                <Mail className="h-4 w-4 mr-2" />
                Manage Contact Messages
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderDownloads = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <Download className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Browse Files</h1>
            <p className="text-gray-600">Access all your documents and forms</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            size="sm" 
            className="border-2 border-gray-300 hover:border-gray-400 transition-all duration-200"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button 
            size="sm" 
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200"
          >
            <Download className="h-4 w-4 mr-2" />
            Download All
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <Card className="border-2 border-blue-100 shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 border-2 border-gray-200 focus:border-blue-400 rounded-xl transition-all duration-200"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={selectedCategoryFilter}
                onChange={(e) => {
                  if (e.target.value === 'all') {
                    setSelectedCategoryFilter('all');
                    setSelectedCategory(null);
                    setDocuments([]);
                  } else {
                    setSelectedCategoryFilter(e.target.value);
                    loadCategoryDocuments(e.target.value);
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-400 rounded-xl shadow-sm focus:outline-none bg-white text-gray-900 transition-all duration-200"
              >
                <option value="all">All Categories</option>
                {categories.map((category, index) => (
                  <option key={category.strGUID || `category-${index}`} value={category.strGUID?.toString() || ''}>{category.strName}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loadingFiles && (
        <Card className="border-2 border-blue-100 shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent"></div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Loading Files</h3>
                <p className="text-gray-600">Please wait while we fetch your documents...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {fileError && (
        <Card className="border-2 border-red-100 shadow-lg">
          <CardContent className="p-6">
            <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl shadow-lg">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-red-800 font-medium">{fileError}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Categories Grid */}
      {!loadingFiles && !fileError && categories.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => {
            // Map icon names to actual icon components
            const getIconComponent = (iconName: string) => {
              switch (iconName) {
                case 'FileCheck':
                  return <FileCheck className="h-5 w-5 text-white" />;
                case 'ClipboardList':
                  return <ClipboardList className="h-5 w-5 text-white" />;
                case 'Shield':
                  return <Shield className="h-5 w-5 text-white" />;
                case 'FileText':
                  return <FileText className="h-5 w-5 text-white" />;
                case 'Bell':
                  return <FileText className="h-5 w-5 text-white" />;
                default:
                  return <FileCheck className="h-5 w-5 text-white" />;
              }
            };

            return (
              <Card 
                key={category.strGUID || `category-${index}`} 
                className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200 cursor-pointer"
                onClick={() => {
                  // console.log('Category clicked:', {
                  //   id: category.strGUID,
                  //   name: category.strName,
                  //   hasGUID: !!category.strGUID
                  // });
                  if (category.strGUID) {
                    loadCategoryDocuments(category.strGUID);
                  } else {
                    // console.error('Category has no GUID:', category);
                  }
                }}
              >
                <CardHeader className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                      {getIconComponent('FileCheck')}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {category.strName}
                      </CardTitle>
                      <CardDescription className="text-sm text-gray-600">
                        {category.downloadCount || 0} files • Sort: {category.intSortOrder}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      Category
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-2 border-blue-200 hover:border-blue-400 transition-all duration-200"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Browse Files
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* No Categories State */}
      {!loadingFiles && !fileError && categories.length === 0 && (
        <Card className="border-2 border-gray-100 shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center space-y-6">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                <FileCheck className="h-10 w-10 text-gray-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Categories Found</h3>
                <p className="text-gray-600">Categories will appear here once they are created</p>
              </div>
              <Button 
                onClick={() => {
                  setActiveTab('downloads');
                  setDownloadsSubTab('categories');
                }}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Category
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents List */}
      {selectedCategory && documents.length > 0 && (
        <Card className="border-2 border-blue-100 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl md:text-2xl font-bold text-gray-900">
                    {selectedCategory.strName} Documents ({documents.length})
                  </CardTitle>
                  <CardDescription className="text-sm text-gray-600">
                    Browse and download all available files in this category
                  </CardDescription>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedCategory(null);
                  setDocuments([]);
                  setSelectedCategoryFilter('all');
                }}
                className="self-start sm:self-auto border-2 hover:border-blue-400 transition-all duration-200"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Categories
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 md:p-6">
            <div className="space-y-4">
              {documents.map((document) => (
                <div key={document.strGUID} className="group hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-blue-200 rounded-xl p-4 md:p-6 bg-white">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                        <FileText className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">
                          {document.strDisplayName}
                        </p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {document.strFileType.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-500">
                            {(document.fileSize / 1024 / 1024).toFixed(2)} MB
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(document.dtUploadDate).toLocaleDateString()}
                          </span>
                          <span className="text-sm text-gray-500 bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                            {document.downloadCount || 0} downloads
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDocumentDownload(document.strGUID)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDocumentDelete(document.strGUID)}
                        className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 transition-all duration-200"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Documents State */}
      {selectedCategory && documents.length === 0 && !loadingFiles && (
        <Card className="border-2 border-gray-100 shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center space-y-6">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                <FileText className="h-10 w-10 text-gray-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Documents Found</h3>
                <p className="text-gray-600">This category doesn't have any documents yet</p>
              </div>
              <Button 
                variant="outline" 
                className="border-2 hover:border-blue-400 transition-all duration-200"
                onClick={() => {
                  setSelectedCategory(null);
                  setDocuments([]);
                  setSelectedCategoryFilter('all');
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Categories
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderProfileModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">User Profile</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowProfileModal(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading profile...</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600">{success}</p>
          </div>
        )}

        {userProfile && !loading && (
          <div className="space-y-6">
            {/* Profile Header */}
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-10 w-10 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{userProfile.strName}</h3>
                <p className="text-gray-600">{userProfile.strEmailId}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    userProfile.bolsActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {userProfile.bolsActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-sm text-gray-500">Provider: {userProfile.authProvider}</span>
                </div>
              </div>
            </div>

            {/* Profile Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={editingProfile ? editingData.strName : userProfile.strName}
                    onChange={(e) => setEditingData({...editingData, strName: e.target.value})}
                    disabled={!editingProfile}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={userProfile.strEmailId}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed as it's used for login</p>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    value={editingProfile ? (editingData.bolsActive?.toString() || 'true') : (userProfile.bolsActive?.toString() || 'true')}
                    onChange={(e) => setEditingData({...editingData, bolsActive: e.target.value === 'true'})}
                    disabled={!editingProfile}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                
                {/* User ID hidden intentionally; it should not be displayed in the UI */}
                
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Created Date</Label>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(userProfile.createDate).toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label>Last Modified</Label>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(userProfile.modifyDate).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center space-x-2">
                {editingProfile ? (
                  <>
                    <Button onClick={handleProfileUpdate} disabled={loading}>
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setEditingProfile(false);
                                              setEditingData({
                        strName: userProfile.strName || '',
                        bolsActive: userProfile.bolsActive
                      });
                      }}
                      disabled={loading}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => setEditingProfile(true)} disabled={loading}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Profile
                    </Button>
                    <Button variant="outline" disabled={loading}>
                      <Lock className="h-4 w-4 mr-2" />
                      Change Password
                    </Button>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  className="text-red-600 hover:text-red-700"
                  onClick={handleProfileDelete}
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {loading ? 'Deleting...' : 'Delete Account'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return renderHome();
      case 'downloads':
        switch (downloadsSubTab) {
          case 'browse':
            return renderDownloads();
          case 'categories':
            return <CategoryManagement />;
          case 'upload':
            return <DocumentUpload onUploadSuccess={loadCategories} />;
          default:
            return renderDownloads();
        }
      case 'circulars':
        return <CircularManagement />;
      case 'other-files':
        return renderOtherFiles();
      case 'contacts':
        return <ContactManagement />;
      default:
        return renderHome();
    }
  };



  const renderOtherFiles = () => <OtherFileManagement />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Mobile Sidebar Overlay */}
        {mobileSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <img 
                    src="/moneycare-logo.png" 
                    alt="MoneyCare Logo" 
                    className="h-8 w-8 flex-shrink-0 object-contain"
                  />
                  {!sidebarCollapsed && (
                    <h1 className="text-xl font-bold text-gray-900">MoneyCare</h1>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="flex-shrink-0"
                >
                  {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            {/* User Profile */}
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                {!sidebarCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {user?.strName ? getFirstName(user.strName) : 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.strEmailId}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="flex-1 p-4 overflow-y-auto">
              <ul className="space-y-2">
                {sidebarItems.map((item) => {
                  const Icon = item.icon;
                  const isDownloads = item.id === 'downloads';
                  const isActive = activeTab === item.id;
                  
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => {
                          setActiveTab(item.id);
                          if (isDownloads) {
                            setDownloadsSubTab('browse');
                          } else {
                            setDownloadsSubTab(null);
                          }
                          // Clear contact notifications when clicking on contacts tab
                          if (item.id === 'contacts') {
                            setNewContactNotifications([]);
                          }
                        }}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          isActive
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title={sidebarCollapsed ? item.label : ''}
                      >
                        <div className="relative">
                          <Icon className="h-5 w-5 flex-shrink-0" />
                          {item.badge && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        {!sidebarCollapsed && <span>{item.label}</span>}
                      </button>
                      
                      {/* Downloads Sub-navigation */}
                      {isDownloads && isActive && !sidebarCollapsed && (
                        <ul className="ml-6 mt-2 space-y-1">
                          {downloadsSubItems.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <li key={subItem.id}>
                                <button
                                  onClick={() => setDownloadsSubTab(subItem.id)}
                                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors text-sm ${
                                    downloadsSubTab === subItem.id
                                      ? 'bg-blue-50 text-blue-600 border-l-2 border-blue-600'
                                      : 'text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  <SubIcon className="h-4 w-4 flex-shrink-0" />
                                  <span>{subItem.label}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Logout Button */}
            <div className="p-4 border-t">
              <Button
                variant="outline"
                onClick={handleLogout}
                className={`w-full flex items-center space-x-2 ${sidebarCollapsed ? 'justify-center' : ''}`}
                title={sidebarCollapsed ? 'Logout' : ''}
              >
                <LogOut className="h-4 w-4 flex-shrink-0" />
                {!sidebarCollapsed && <span>Logout</span>}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          {/* Contact Notification Banner */}
          {showContactBanner && latestContact && (
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-l-4 border-emerald-400 p-6 shadow-lg animate-in slide-in-from-top duration-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-emerald-900 mb-1">
                      New Message Received! 📬
                    </h3>
                    <p className="text-sm text-emerald-800 mb-1">
                      <span className="font-medium">{latestContact.name}</span> has sent you a message
                    </p>
                    <p className="text-sm text-emerald-700">
                      <span className="font-medium">Subject:</span> {latestContact.subject}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setActiveTab('contacts');
                      setShowContactBanner(false);
                    }}
                    className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 transition-colors duration-200"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    View Message
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowContactBanner(false)}
                    className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 transition-colors duration-200"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <header className="bg-white shadow-sm border-b">
            <div className="px-4 md:px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    {/* Mobile Menu Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMobileSidebarOpen(true)}
                      className="lg:hidden self-start"
                    >
                      <Menu className="h-5 w-5" />
                    </Button>
                    
                    <div className="flex items-center space-x-3">
                      <img 
                        src="/moneycare-logo.png" 
                        alt="MoneyCare Logo" 
                        className="h-8 w-8 object-contain"
                      />
                      <div>
                        <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900">
                          {activeTab === 'downloads' && downloadsSubTab 
                            ? downloadsSubItems.find(item => item.id === downloadsSubTab)?.label
                            : sidebarItems.find(item => item.id === activeTab)?.label
                          }
                        </h2>
                        <p className="text-sm md:text-base text-gray-600">
                          Welcome back, {user?.strName ? getFirstName(user.strName) : 'User'}!
                        </p>
                      </div>
                    </div>
                    
                    {/* Global Search and Refresh */}
                    {activeTab === 'home' && (
                      <div className="flex items-center space-x-4 w-full sm:w-auto sm:ml-8">
                        <div className="relative flex-1 sm:flex-none">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input
                            type="text"
                            placeholder="Search documents, circulars..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-80"
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadDashboardStatistics}
                          disabled={loadingStats}
                          className="flex items-center space-x-2"
                        >
                          <RotateCcw className={`h-4 w-4 ${loadingStats ? 'animate-spin' : ''}`} />
                          <span className="hidden sm:inline">Refresh</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Notifications */}


                {/* Profile Dropdown */}
                <div className="relative">
                  <Button
                    variant="ghost"
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className="flex items-center space-x-2"
                  >
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-3 w-3 md:h-4 md:w-4 text-blue-600" />
                    </div>
                    <span className="text-xs md:text-sm font-medium hidden sm:block">
                      {user?.strName ? getFirstName(user.strName) : 'User'}
                    </span>
                    <ChevronDown className="h-3 w-3 md:h-4 md:w-4 hidden sm:block" />
                  </Button>
                  
                  {profileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 md:w-64 bg-white rounded-lg shadow-lg border z-50">
                      <div className="p-3 md:p-4 border-b">
                        <div className="flex items-center space-x-2 md:space-x-3">
                          <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm md:text-base">{user?.strName || 'User'}</p>
                            <p className="text-xs md:text-sm text-gray-500">{user?.strEmailId}</p>
                          </div>
                        </div>
                      </div>
                      <div className="p-2">
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setProfileDropdownOpen(false);
                            handleProfileModalOpen();
                          }}
                          className="w-full justify-start text-xs md:text-sm"
                        >
                          <User className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                          View Profile
                        </Button>

                        <Button
                          variant="ghost"
                          onClick={() => {
                            setProfileDropdownOpen(false);
                            handleLogout();
                          }}
                          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 text-xs md:text-sm"
                        >
                          <LogOut className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                          Logout
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 p-4 md:p-6">
            {renderContent()}
          </main>
        </div>
      </div>

      {/* Profile Modal */}
      {showProfileModal && renderProfileModal()}

      {/* Click outside to close dropdowns */}
      {profileDropdownOpen && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => {
            setProfileDropdownOpen(false);
          }}
        />
      )}

      {/* Toast Notifications */}
      {showToast && (success || error) && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
          <div className={`flex items-center space-x-4 p-4 rounded-xl shadow-xl border-2 backdrop-blur-sm ${
            success 
              ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200 text-emerald-800' 
              : 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-800'
          }`}>
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              success ? 'bg-emerald-100' : 'bg-red-100'
            }`}>
              {success ? (
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">
                {success || error}
              </p>
            </div>
            <button
              onClick={() => setShowToast(false)}
              className={`flex-shrink-0 p-1 rounded-full hover:bg-opacity-20 transition-colors duration-200 ${
                success ? 'hover:bg-emerald-200' : 'hover:bg-red-200'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 